// IntelligenceWorkspace.js — Content Intelligence Specialist queue surface.
//
// Mount sequence:
//   1. useEffect → GET /api/intelligence/me with Bearer staff token.
//      • 200 → setStaff (header renders) → fetch first /queue page.
//      • 401 → token is stale OR specialist deactivated. Clear
//        localStorage and redirect to /intelligence/login. THIS closes
//        the surgical freshness-check loop end-to-end: a fired
//        specialist's still-valid JWT is rejected by /me at the
//        backend, frontend evicts on next page load.
//   2. Render header (name + role badge + logout) and queue list.
//   3. "Load more" → GET /api/intelligence/queue?offset=N&limit=N,
//      append to current rows.
//
// HEADER CONTENT IS DELIBERATELY MINIMAL:
//   • Workspace title ("CONTENT INTELLIGENCE")
//   • Staff name + role badge
//   • Logout button
//   NO navigation links. NO references to /dashboard, /admin, or any
//   other route. The absence is the structural enforcement of
//   least-privilege — see the role memory.
//
// SLICE SCOPE: read-only queue display. The "Review" button on each
// row is intentionally disabled — verdict submission ships in the
// next slice (card view + POST /api/intelligence/verify wiring).

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

const API = 'https://tribaltech-backend.onrender.com';
const PAGE_SIZE = 20;

function bearerHeaders() {
  const token = localStorage.getItem('tribal_staff_token');
  return { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };
}

function evictToLogin() {
  localStorage.removeItem('tribal_staff_token');
  localStorage.removeItem('tribal_staff_user');
  window.location.href = '/intelligence/login';
}

// Tier visual mapping. See specialist_review_queue RPC for the tier
// derivation rules.
const TIER_META = {
  1: { label: 'Conflict + username', color: '#ef4444', bg: 'rgba(239,68,68,0.10)' },
  2: { label: 'Conflict',            color: '#f59e0b', bg: 'rgba(245,158,11,0.10)' },
  3: { label: 'Signal missing',      color: '#eab308', bg: 'rgba(234,179,8,0.10)' },
  4: { label: 'Clean positive',      color: '#22c55e', bg: 'rgba(34,197,94,0.10)' },
};

// Role display labels (badge in header) + endpoint routing.
// Phase 3 Slice 2: operator gets the attribution-conflict queue;
// specialist keeps the legacy review queue. The endpoint decision lives
// here so the queue page is the only thing that branches on role —
// IntelligenceReview.js stays role-agnostic (Slice 4 extends the card).
const ROLE_LABEL = {
  content_intelligence_specialist: 'Specialist',
  intelligence_operator:           'Operator',
};

function endpointForRole(role) {
  return role === 'intelligence_operator' ? 'attribution-queue' : 'queue';
}

// Pretty labels for the gate's failed_gate values shown in the
// attribution-queue table. The raw enum stays in the data; the UI
// renders the human form.
const FAILED_GATE_LABEL = {
  username_disagrees: 'Username disagrees',
  competing_creator:  'Competing creator',
};

function relativeTime(iso) {
  if (!iso) return '—';
  const ms = Date.now() - new Date(iso).getTime();
  const m = Math.floor(ms / 60000);
  if (m < 1)    return 'just now';
  if (m < 60)   return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24)   return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30)   return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
}

function truncate(s, n) {
  if (!s) return '';
  return s.length > n ? s.slice(0, n - 1) + '…' : s;
}

export default function IntelligenceWorkspace() {
  const [staff,         setStaff]         = useState(null);
  const [rows,          setRows]          = useState([]);
  const [offset,        setOffset]        = useState(0);
  const [hasMore,       setHasMore]       = useState(false);
  const [loading,       setLoading]       = useState(true);   // initial /me + first queue load
  const [loadingMore,   setLoadingMore]   = useState(false);
  const [error,         setError]         = useState('');

  // Initial mount: /me freshness check → first queue page
  useEffect(() => {
    (async () => {
      try {
        const meRes = await fetch(`${API}/api/intelligence/me`,
          { headers: bearerHeaders() });
        if (meRes.status === 401) {
          // Either bad/missing token OR deactivated staff. Evict.
          evictToLogin();
          return;
        }
        if (!meRes.ok) {
          setError('Could not load your profile. Please try again.');
          setLoading(false);
          return;
        }
        const me = await meRes.json();
        setStaff(me);
        // First queue page. Pass me.role explicitly because setStaff is
        // async — staff?.role inside loadQueue's closure isn't set yet
        // on this first call. Subsequent calls (Load more) read role
        // from staff?.role via closure as normal.
        await loadQueue(0, true, me.role);
      } catch (e) {
        setError('Network error. Please refresh.');
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function loadQueue(nextOffset, isFirstLoad, roleOverride) {
    if (isFirstLoad) setLoading(true);
    else             setLoadingMore(true);
    try {
      const role = roleOverride || staff?.role;
      const path = endpointForRole(role);
      const url  = `${API}/api/intelligence/${path}?limit=${PAGE_SIZE}&offset=${nextOffset}`;
      const res = await fetch(url, { headers: bearerHeaders() });
      if (res.status === 401) { evictToLogin(); return; }
      if (!res.ok) {
        setError('Could not load the queue.');
        return;
      }
      const data = await res.json();
      setRows(prev => isFirstLoad ? (data.rows || []) : [...prev, ...(data.rows || [])]);
      setOffset(nextOffset + (data.rows?.length || 0));
      setHasMore(!!data.has_more);
    } catch {
      setError('Network error loading queue.');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }

  function handleLogout() {
    evictToLogin();
  }

  function handleLoadMore() {
    loadQueue(offset, false);
  }

  // ─── Render ──────────────────────────────────────────────────────
  return (
    <div style={pageStyle}>
      <Header staff={staff} onLogout={handleLogout} />

      <main style={mainStyle}>
        {loading && <div style={muted}>Loading queue…</div>}

        {!loading && error && (
          <div style={errorBox}>{error}</div>
        )}

        {!loading && !error && rows.length === 0 && (
          <div style={emptyBox}>
            <div style={{ fontSize: 16, color: '#94a3b8', marginBottom: 6 }}>
              No leaks awaiting review.
            </div>
            <div style={{ fontSize: 13, color: '#666' }}>
              The queue will populate as the gate routes leaks for human verification.
            </div>
          </div>
        )}

        {!loading && rows.length > 0 && (
          <>
            {staff?.role === 'intelligence_operator'
              ? <AttributionQueueTable rows={rows} />
              : <QueueTable             rows={rows} />}
            <div style={{ marginTop: 18, textAlign: 'center' }}>
              {hasMore ? (
                <button
                  onClick={handleLoadMore}
                  disabled={loadingMore}
                  style={loadMoreButton}
                >
                  {loadingMore ? 'Loading…' : 'Load more'}
                </button>
              ) : (
                <div style={{ ...muted, fontSize: 12 }}>End of queue.</div>
              )}
            </div>
          </>
        )}
      </main>
    </div>
  );
}

function Header({ staff, onLogout }) {
  return (
    <header style={headerStyle}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 14 }}>
        <div style={{ fontSize: 13, fontWeight: 700, letterSpacing: 2,
                       color: '#fff', textTransform: 'uppercase' }}>
          Content Intelligence
        </div>
        <div style={{ fontSize: 11, color: '#888', letterSpacing: 1.5,
                       textTransform: 'uppercase' }}>
          Creator Protect
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
        {staff && (
          <>
            <div style={{ color: '#fff', fontSize: 14 }}>
              {staff.full_name || staff.email}
            </div>
            <div style={roleBadge}>
              {ROLE_LABEL[staff.role] || 'Staff'}
            </div>
          </>
        )}
        <button onClick={onLogout} style={logoutButton}>
          Logout
        </button>
      </div>
    </header>
  );
}

function QueueTable({ rows }) {
  return (
    <div style={tableShell}>
      <div style={tableHeaderRow}>
        <div style={{ ...col, width: 180 }}>Tier</div>
        <div style={{ ...col, flex: 2 }}>Host · URL</div>
        <div style={{ ...col, width: 90,  textAlign: 'right' }}>Hamming</div>
        <div style={{ ...col, width: 110, textAlign: 'right' }}>Competitors</div>
        <div style={{ ...col, width: 110 }}>Detected</div>
        <div style={{ ...col, width: 100, textAlign: 'right' }}></div>
      </div>

      {rows.map(row => {
        const tier = TIER_META[row.tier] || TIER_META[4];
        return (
          <div key={row.id} style={tableRow}>
            <div style={{ ...col, width: 180 }}>
              <span style={{
                display: 'inline-block',
                padding: '3px 10px',
                borderRadius: 4,
                background: tier.bg,
                color: tier.color,
                fontSize: 11,
                fontWeight: 600,
                letterSpacing: 0.4,
              }}>
                {tier.label}
              </span>
              {row.review_reason_code && (
                <div style={{ fontSize: 10, color: '#666', marginTop: 4 }}>
                  {row.review_reason_code}
                </div>
              )}
            </div>

            <div style={{ ...col, flex: 2, overflow: 'hidden' }}>
              <div style={{ color: '#94a3b8', fontSize: 12 }}>
                {row.source_host || '(no host)'}
              </div>
              <a href={row.leaked_url} target="_blank" rel="noreferrer"
                 title={row.leaked_url}
                 style={{
                   color: '#a78bfa', fontSize: 13,
                   textDecoration: 'none', wordBreak: 'break-all',
                 }}>
                {truncate(row.leaked_url, 80)}
              </a>
            </div>

            <div style={{ ...col, width: 90,
                           textAlign: 'right', color: '#fff', fontSize: 14 }}>
              {row.best_hamming != null ? row.best_hamming : '—'}
            </div>

            <div style={{ ...col, width: 110,
                           textAlign: 'right', color: '#fff', fontSize: 14 }}>
              {row.competing_creators != null ? row.competing_creators : '—'}
            </div>

            <div style={{ ...col, width: 110, color: '#94a3b8', fontSize: 12 }}>
              {relativeTime(row.detected_at)}
            </div>

            <div style={{ ...col, width: 100, textAlign: 'right' }}>
              <Link to={`/intelligence/review/${row.id}`} state={{ row }}
                    style={reviewButton}>
                Review
              </Link>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function AttributionQueueTable({ rows }) {
  // Operator's queue. Row shape comes from /api/intelligence/attribution-
  // queue → attribution_review_queue RPC, NOT specialist_review_queue.
  // Key field is leak_id (not id) — the Review link uses leak_id and the
  // state.row passed to IntelligenceReview is the attribution row shape.
  // Slice 4 extends the review card to render attribution rows natively;
  // until then, the card's existing fields (leaked_url, source_host,
  // best_hamming, detected_at) overlap, so click-through is functional
  // even if tier/competing_creators read as "—".
  return (
    <div style={tableShell}>
      <div style={tableHeaderRow}>
        <div style={{ ...col, width: 140 }}>Filed as</div>
        <div style={{ ...col, width: 140 }}>Visual match</div>
        <div style={{ ...col, width: 130 }}>Conflict</div>
        <div style={{ ...col, width: 60,  textAlign: 'right' }}>h</div>
        <div style={{ ...col, width: 60,  textAlign: 'right' }}>gap</div>
        <div style={{ ...col, flex: 2 }}>Host · URL</div>
        <div style={{ ...col, width: 110 }}>Detected</div>
        <div style={{ ...col, width: 100, textAlign: 'right' }}></div>
      </div>

      {rows.map(row => (
        <div key={row.leak_id} style={tableRow}>
          {/* Filed-as creator (the existing, suspect attribution). Muted
              so the eye lands on the Visual-match column next door. */}
          <div style={{ ...col, width: 140, color: '#94a3b8', fontSize: 13 }}>
            {row.current_creator_stage || '—'}
          </div>

          {/* Visual-match creator (the gate's actual best match). Purple
              + bold so the operator can scan the two columns and spot
              "filed as A but visual says B" at a glance. */}
          <div style={{ ...col, width: 140, color: '#a78bfa',
                         fontSize: 13, fontWeight: 600 }}>
            {row.best_handle || '—'}
          </div>

          {/* Refusal reason from the gate. The two valid values are
              username_disagrees / competing_creator (RPC filter). */}
          <div style={{ ...col, width: 130 }}>
            <span style={attributionConflictBadge}>
              {FAILED_GATE_LABEL[row.failed_gate] || row.failed_gate || '—'}
            </span>
          </div>

          {/* Hamming + bimodal_gap — the visual-evidence numbers.
              Lower hamming + bigger gap = stronger conflict signal. */}
          <div style={{ ...col, width: 60, textAlign: 'right',
                         color: '#fff', fontSize: 14 }}>
            {row.best_hamming != null ? row.best_hamming : '—'}
          </div>
          <div style={{ ...col, width: 60, textAlign: 'right',
                         color: '#fff', fontSize: 14 }}>
            {row.bimodal_gap != null ? row.bimodal_gap : '—'}
          </div>

          {/* Host + URL — same shape as the specialist table. */}
          <div style={{ ...col, flex: 2, overflow: 'hidden' }}>
            <div style={{ color: '#94a3b8', fontSize: 12 }}>
              {row.source_host || '(no host)'}
            </div>
            <a href={row.leaked_url} target="_blank" rel="noreferrer"
               title={row.leaked_url}
               style={{
                 color: '#a78bfa', fontSize: 13,
                 textDecoration: 'none', wordBreak: 'break-all',
               }}>
              {truncate(row.leaked_url, 80)}
            </a>
          </div>

          <div style={{ ...col, width: 110, color: '#94a3b8', fontSize: 12 }}>
            {relativeTime(row.detected_at)}
          </div>

          {/* Review link uses leak_id (the attribution-queue PK column
              name; specialist queue uses id). The state shape passed
              through is the attribution row; the card renders what it
              can and Slice 4 wires the rest. */}
          <div style={{ ...col, width: 100, textAlign: 'right' }}>
            <Link to={`/intelligence/review/${row.leak_id}`} state={{ row }}
                  style={reviewButton}>
              Review
            </Link>
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Styles ──────────────────────────────────────────────────────
const pageStyle = {
  minHeight: '100vh',
  background: 'linear-gradient(180deg, #0f0f1e 0%, #0a0a0f 100%)',
  color: '#fff',
};

const headerStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '16px 32px',
  borderBottom: '1px solid #222',
  background: 'rgba(15,15,30,0.6)',
};

const roleBadge = {
  padding: '3px 10px',
  borderRadius: 4,
  background: 'rgba(124,58,237,0.15)',
  color: '#a78bfa',
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: 1,
  textTransform: 'uppercase',
};

// Attribution-queue Conflict column badge. Amber, mirrors Tier-2
// "Conflict" colour from TIER_META so the two queues feel like the same
// design system even though their column shapes diverge.
const attributionConflictBadge = {
  display: 'inline-block',
  padding: '3px 10px',
  borderRadius: 4,
  background: 'rgba(245,158,11,0.10)',
  color: '#f59e0b',
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: 0.4,
};

const logoutButton = {
  padding: '7px 14px',
  borderRadius: 6,
  background: '#1a1a1a',
  border: '1px solid #2a2a2a',
  color: '#94a3b8',
  fontSize: 13,
  cursor: 'pointer',
};

const mainStyle = {
  maxWidth: 1200,
  margin: '0 auto',
  padding: '32px 24px',
};

const tableShell = {
  background: '#111',
  border: '1px solid #222',
  borderRadius: 12,
  overflow: 'hidden',
};

const tableHeaderRow = {
  display: 'flex',
  padding: '12px 16px',
  borderBottom: '1px solid #222',
  background: '#0d0d18',
  fontSize: 11,
  color: '#666',
  letterSpacing: 1.2,
  textTransform: 'uppercase',
};

const tableRow = {
  display: 'flex',
  padding: '14px 16px',
  borderBottom: '1px solid #1a1a1a',
  alignItems: 'flex-start',
};

const col = {
  padding: '0 8px',
};

const muted = {
  color: '#666',
  fontSize: 14,
  textAlign: 'center',
  padding: '40px 0',
};

const errorBox = {
  padding: '14px 18px',
  background: 'rgba(248,113,113,0.08)',
  border: '1px solid rgba(248,113,113,0.3)',
  borderRadius: 8,
  color: '#f87171',
  fontSize: 14,
};

const emptyBox = {
  padding: '60px 24px',
  textAlign: 'center',
  background: '#111',
  border: '1px solid #222',
  borderRadius: 12,
};

const loadMoreButton = {
  padding: '10px 24px',
  borderRadius: 8,
  border: '1px solid #2a2a2a',
  background: '#1a1a1a',
  color: '#94a3b8',
  fontSize: 13,
  cursor: 'pointer',
};

const reviewButton = {
  padding: '6px 14px',
  borderRadius: 6,
  background: 'rgba(124,58,237,0.15)',
  border: '1px solid rgba(124,58,237,0.4)',
  color: '#a78bfa',
  fontSize: 12,
  fontWeight: 600,
  cursor: 'pointer',
  textDecoration: 'none',
  display: 'inline-block',
};
